import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faVolumeHigh,
    faVolumeLow,
    faVolumeMedium,
    faVolumeOff,
    faVolumeSlash,
} from "@fortawesome/pro-regular-svg-icons";
import "./volumeControls.scss";
import { useTranslation } from "react-i18next";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { generateUUID } from "userful-chronos-app-common-js/dist/utils";
import { UClientComp } from "userful-chronos-app-common-js/dist/models/uclient";
import { REQUEST_UPDATE_UCLIENT_AUDIO } from "userful-chronos-app-common-js/dist/message/messageTypeDefinitions/displays/UClientsMsgDefinations";
import { StringID } from "userful-chronos-app-common-js/dist/models/common";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { AudioOffset, AudioSettings } from "userful-chronos-app-common-js/dist/models/cdm/CdmAudio";

const MAX_VOLUME_LEVEL = 100.0;
const MIN_VOLUME_LEVEL = 0;

interface IProps {
    uclients: UClientComp[];
    disabled?: boolean;
    label?: string;
}

const updateAudioSettings = (id: StringID, settings: AudioSettings) => {
    getGlobalStates().eventBus.sendMsg(REQUEST_UPDATE_UCLIENT_AUDIO, { id, settings });
};

export default function MultiUClientVolumeControls({ uclients, disabled, label }: IProps) {
    const { t } = useTranslation();

    // We'll use the first client's settings as the base for our UI
    // but apply changes to all clients
    const referenceClient = uclients[0];

    const defaultAudioSettings: AudioSettings = {
        audioVolume: { muted: false, level: 50 },
        audioOffset: { offset: 0 }, // Duration can be a number
    };

    const [audioSettings, setAudioSettings] = useState<AudioSettings>(
        referenceClient ? referenceClient.audioSettings : defaultAudioSettings,
    );
    const [lockUpdate, setLockUpdate] = useState(false);
    const lockRef = useRef(null);

    useEffect(() => {
        if (!lockUpdate && referenceClient) {
            setAudioSettings(referenceClient.audioSettings);
        }
    }, [referenceClient, lockUpdate]);

    const lock = () => {
        clearTimeout(lockRef.current);
        setLockUpdate(true);
        lockRef.current = setTimeout(() => setLockUpdate(false), 1000);
    };

    const getIcon = function (): IconProp {
        let icon = faVolumeSlash;
        let muted = audioSettings.audioVolume.muted;
        let level = audioSettings.audioVolume.level;

        if (!muted) {
            if (level === 0) icon = faVolumeOff;
            else if (level <= 25) icon = faVolumeLow;
            else if (level <= 75) icon = faVolumeMedium;
            else icon = faVolumeHigh;
        }
        return icon;
    };

    const handleMuteButtonClicked = function (e) {
        if (uclients.length === 0) return;

        lock();
        const muted = !audioSettings.audioVolume.muted;
        const level = audioSettings.audioVolume.level;
        const audioOffset = audioSettings.audioOffset;

        const nAudioSettings = { audioVolume: { muted, level }, audioOffset };
        setAudioSettings(nAudioSettings);

        // Apply to all clients
        uclients.forEach((client) => {
            updateAudioSettings(client.id, nAudioSettings);
        });
    };

    const handleChange = function (e: any) {
        if (uclients.length === 0) return;

        lock();
        const muted = audioSettings.audioVolume.muted;
        const audioOffset = audioSettings.audioOffset;
        const value = e.target.value;

        const nAudioSettings = { audioVolume: { muted, level: value }, audioOffset };
        setAudioSettings(nAudioSettings);

        // Apply to all clients
        uclients.forEach((client) => {
            updateAudioSettings(client.id, nAudioSettings);
        });
    };

    if (uclients.length === 0) {
        return null;
    }

    return (
        <div
            className="volumeControlWrapper"
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        >
            {/* {label && <div className="volume-control-label">{label}</div>} */}
            <div className={`sourceInteractiveViewerLink`} onClick={handleMuteButtonClicked}>
                <div className="iconInside">
                    <FontAwesomeIcon icon={getIcon()} />
                </div>
            </div>
            <Form.Range
                id={"MULTI_VOL_CTRL_" + generateUUID()}
                defaultValue={audioSettings.audioVolume.level}
                value={audioSettings.audioVolume.level}
                min={MIN_VOLUME_LEVEL}
                max={MAX_VOLUME_LEVEL}
                onChange={handleChange}
                bsPrefix="volume-controls-slider"
                disabled={disabled}
            />
        </div>
    );
}
